import React, { useEffect, useRef } from 'react';
import { startStripeCheckout } from '../utils/upgradeFlow';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import MobileDesktopBanner from '../components/MobileDesktopBanner';
import { DLC_COLORS, dlcRgba } from '../constants/dlcColors';
import './PtcbPrep.css';

// Counts derived from services/ptcb_content.py + the in-app domain map
// (2026-06-12) — MAP-LITERAL semantics: lessons = topic groups (one teaching
// lesson each), synthesis = inter-group tier links, bosses = fightable boss
// nodes the map renders (one group boss per group + one tier boss per link +
// the domain apothecarius), rounds = clinical rounds. Previously authored
// estimates.
const DOMAINS = [
  { name: 'Medications', color: '#60a5fa', lessons: 70, synthesis: 2, bosses: 73, rounds: 1 },
  { name: 'Federal Requirements', color: '#38bdf8', lessons: 35, synthesis: 1, bosses: 37, rounds: 1 },
  { name: 'Patient Safety', color: '#22d3ee', lessons: 47, synthesis: 1, bosses: 49, rounds: 1 },
  { name: 'Order Entry', color: '#818cf8', lessons: 39, synthesis: 1, bosses: 41, rounds: 1 },
];

const CheckIcon = () => (
  <svg className="naplex-prep-compare-icon" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg className="naplex-prep-compare-icon" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ArrowIcon = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default function PtcbPrep() {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const observerRef = useRef(null);
  const isAliasRoute = location.pathname === '/ptcb';
  const canonicalUrl = 'https://debono.ai/ptcb-prep';
  const robotsValue = isAliasRoute ? 'noindex, follow' : 'index, follow';

  useEffect(() => {
    const ref = searchParams.get('ref');
    const utmSource = searchParams.get('utm_source');
    const utmCampaign = searchParams.get('utm_campaign');
    if (ref || utmSource || utmCampaign) {
      try {
        localStorage.setItem('debono_ref', JSON.stringify({
          ref,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
          ts: Date.now(),
        }));
      } catch (_) {}
    }
  }, [searchParams]);

  useEffect(() => {
    const els = document.querySelectorAll('.naplex-animate');
    if (!els.length) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('naplex-visible');
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach((el) => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  const handleCta = () => {
    if (isAuthenticated) {
      if (user?.ptcb_dlc) {
        navigate('/dashboard?ptcb=1');
      } else {
        startStripeCheckout('ptcb_crunch', { fallbackNavigate: navigate });
      }
    } else {
      navigate('/signup?ptcb=1');
    }
  };

  return (
    <div className="naplex-prep ptcb-prep">
      <Helmet>
        <title>PTCB DLC: AI-Powered PTCB Prep | Debono RX</title>
        <meta
          name="description"
          content="4 PTCB domains with procedural question generation, workflow boss fights, law drills, and adaptive learning maps. One-time $35 purchase, permanent access."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content={robotsValue} />
        <meta property="og:title" content="PTCB DLC: AI-Powered PTCB Prep | Debono RX" />
        <meta
          property="og:description"
          content="4 PTCB domains. Unlimited generated questions. Workflow boss fights. $35 one-time, permanent access."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Debono RX" />
        <meta property="og:image" content="https://debono.ai/og/ptcb.png?v=2" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://debono.ai/og/ptcb.png?v=2" />
      </Helmet>

      <div className="naplex-prep-bg">
        <div className="naplex-prep-glow naplex-prep-glow-1" />
        <div className="naplex-prep-glow naplex-prep-glow-2" />
        <div className="naplex-prep-glow naplex-prep-glow-3" />
      </div>

      <nav className="naplex-prep-topbar">
        <div className="naplex-prep-topbar-container">
          <Link to="/" className="naplex-prep-topbar-logo">
            <span className="naplex-prep-topbar-bono">DEBONO</span>
            <img src={`${process.env.PUBLIC_URL}/favicon-32x32.png?v=7`} alt="" className="naplex-prep-topbar-favicon" />
            <span className="naplex-prep-topbar-rx">rx</span>
          </Link>
          <div className="naplex-prep-topbar-links">
            <Link to="/pricing">Pricing</Link>
            {isAuthenticated ? (
              <Link to="/dashboard?ptcb=1" className="naplex-prep-topbar-cta">Dashboard</Link>
            ) : (
              <>
                <Link to="/login">Log In</Link>
                <Link to="/signup?ptcb=1" className="naplex-prep-topbar-cta">Sign Up</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      <div className="naplex-prep-inner">
        <section className="naplex-prep-hero">
          <div className="naplex-prep-badge naplex-animate">Permanent DLC · One-Time Purchase</div>
          <h1 className="naplex-animate">
            <span>PTCB Prep</span>, Rewired
          </h1>
          <p className="naplex-prep-hero-sub naplex-animate">
            Four testable PTCB domains, rebuilt as a live combat board. Train medications,
            law, workflow, and safety with fresh AI-generated questions, procedural boss fights,
            and learning maps that lock onto what you missed.
          </p>
          <div className="naplex-prep-hero-cta naplex-animate">
            <button className="naplex-prep-btn-primary" onClick={handleCta}>
              {user?.ptcb_dlc ? 'Already Unlocked' : 'Get PTCB DLC · $35'}
            </button>
            <Link to="/pricing" className="naplex-prep-btn-ghost">
              See All Plans
            </Link>
          </div>
        </section>

        <div className="naplex-prep-stats naplex-animate">
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">4</span>
            <span className="naplex-prep-stat-label">Exam Domains</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">1,237</span>
            <span className="naplex-prep-stat-label">Core Topics</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">200</span>
            <span className="naplex-prep-stat-label">Workflow Bosses</span>
          </div>
          <div className="naplex-prep-stat">
            <span className="naplex-prep-stat-value">$35</span>
            <span className="naplex-prep-stat-label">One-Time, Forever</span>
          </div>
        </div>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>How It Works</h2>
            <p>
              Start with a diagnostic hit. Fail fast, surface the exact domain gaps, then
              drill back through a focused map until the workflow is clean.
            </p>
          </div>

          <div className="naplex-prep-flow">
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--1">1</div>
              <h3>Diagnostic Boss Opener</h3>
              <p>
                Choose a PTCB domain and enter a first-pass diagnostic. Medications,
                federal rules, patient safety, and order entry all get pressure tested
                before the map opens.
              </p>
            </div>
            <div className="naplex-prep-flow-arrow naplex-animate"><ArrowIcon /></div>
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--2">2</div>
              <h3>Targeted Workflow Map</h3>
              <p>
                Every miss generates a focused recovery route. You study only the
                medication classes, federal requirements, or safety checkpoints
                that actually broke your run.
              </p>
            </div>
            <div className="naplex-prep-flow-arrow naplex-animate"><ArrowIcon /></div>
            <div className="naplex-prep-flow-card naplex-animate">
              <div className="naplex-prep-flow-num naplex-prep-flow-num--3">3</div>
              <h3>Boss Mastery</h3>
              <p>
                Clear domain bosses, cross-domain synthesis fights, and final workflow
                checks that force accurate order entry, DEA logic, safety flags, and
                product selection under pressure.
              </p>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>PTCB Scope. No Dead Repeats.</h2>
            <p>
              The board covers the four official domains, but the question stream stays alive:
              fresh generations, adaptive retrieval, and boss encounters that change when you retry.
            </p>
          </div>

          <div className="naplex-prep-yield">
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--high">High Yield</span>
                Medications & Calculations
              </h3>
              <p>The drug knowledge and math that drives the largest share of the exam.</p>
              <ul className="naplex-prep-yield-list">
                <li>Brand and generic matching, dosage forms, and therapeutic use</li>
                <li>Top 200 recall with storage, compounding, and route details</li>
                <li>Alligation, day supply, flow rate, and concentration math</li>
                <li>LASA traps, schedule class, REMS, and refill limits</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--medium">Medium Yield</span>
                Workflow & Verification
              </h3>
              <p>Execution pressure: what happens when a safe process breaks in motion.</p>
              <ul className="naplex-prep-yield-list">
                <li>Order entry, adjudication, and product selection sequences</li>
                <li>Duplicate therapy, interaction, allergy, and refill rejections</li>
                <li>Barcode, storage, and controlled substance inventory handling</li>
                <li>Escalation steps for technician-versus-pharmacist boundaries</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--low">Low Yield</span>
                Regulatory Precision
              </h3>
              <p>The law details people forget until the exam punishes them for it.</p>
              <ul className="naplex-prep-yield-list">
                <li>DEA form handling, transfer windows, and partial-fill rules</li>
                <li>Labeling language, record retention, and recall workflows</li>
                <li>HIPAA edge cases, hazardous waste, and package integrity checks</li>
                <li>State-versus-federal technician scope decision points</li>
              </ul>
            </div>
            <div className="naplex-prep-yield-card naplex-animate">
              <h3>
                <span className="naplex-prep-yield-tag naplex-prep-yield-tag--high">Adaptive</span>
                Gap Targeting
              </h3>
              <p>The system prioritizes your actual misses instead of forcing a full reset every time.</p>
              <ul className="naplex-prep-yield-list">
                <li>Failed questions immediately feed the next learning map</li>
                <li>Boss sessions escalate into the areas you keep missing</li>
                <li>Free-domain entry lets you sample one domain before committing</li>
                <li>Retry runs stay fresh instead of recycling a dead bank</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>4 Exam Domains</h2>
            <p>
              The full PTCB outline, each domain with its own lessons, cross-check nodes,
              boss layers, and scenario rounds.
            </p>
          </div>

          <div className="naplex-prep-areas-grid">
            {DOMAINS.map((domain) => (
              <div
                key={domain.name}
                className="naplex-prep-area-card naplex-animate"
                style={{ '--area-color': domain.color }}
              >
                <div className="naplex-prep-area-card-glow" />
                <div className="naplex-prep-area-header">
                  <div className="naplex-prep-area-dot" />
                  <div className="naplex-prep-area-name">{domain.name}</div>
                </div>
                <div className="naplex-prep-area-nodes">
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.lessons} teaching lessons`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                    <span>{domain.lessons}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.synthesis} synthesis nodes`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    <span>{domain.synthesis}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.bosses} boss fights`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    <span>{domain.bosses}</span>
                  </div>
                  <div className="naplex-prep-area-node" role="img" aria-label={`${domain.rounds} scenario rounds`}>
                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                    <span>{domain.rounds}</span>
                  </div>
                </div>
              </div>
            ))}
            <div
              className="naplex-prep-area-card naplex-prep-area-card--totals naplex-animate"
              style={{ '--area-color': '#3b82f6' }}
            >
              <div className="naplex-prep-area-card-glow" />
              <div className="naplex-prep-area-header">
                <div className="naplex-prep-area-dot" />
                <div className="naplex-prep-area-name">Total Coverage</div>
              </div>
              <div className="naplex-prep-area-nodes">
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.lessons, 0)}</span>
                  <span className="naplex-prep-area-node--label">lessons</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.synthesis, 0)}</span>
                  <span className="naplex-prep-area-node--label">links</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.bosses, 0)}</span>
                  <span className="naplex-prep-area-node--label">bosses</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                  <span>{DOMAINS.reduce((sum, domain) => sum + domain.rounds, 0)}</span>
                  <span className="naplex-prep-area-node--label">rounds</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                  <span>4</span>
                  <span className="naplex-prep-area-node--label">domains</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3"/></svg>
                  <span>1,237</span>
                  <span className="naplex-prep-area-node--label">topics</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                  <span>191</span>
                  <span className="naplex-prep-area-node--label">safety flags</span>
                </div>
                <div className="naplex-prep-area-node">
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r=".5"/></svg>
                  <span>{'\u221E'}</span>
                  <span className="naplex-prep-area-node--label">questions</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>What You Encounter</h2>
            <p>
              The same stacked-glass combat rhythm as the NAPLEX product, tuned for retail workflow,
              law precision, medication recall, and safety verification.
            </p>
          </div>

          <div className="naplex-prep-nodes">
            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: dlcRgba('ptcb', 0.14) }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DLC_COLORS.ptcb.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>Diagnostic Boss Openers</h3>
              <p>
                Fast domain checks that expose weak law recall, safety misses,
                order-entry mistakes, and top-200 holes before the learning map opens.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: dlcRgba('ptcb', 0.12) }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DLC_COLORS.ptcb.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
                </svg>
              </div>
              <h3>Medication Mastery Sessions</h3>
              <p>
                Focused review on brand/generic mapping, schedules, dosage forms,
                high-alert meds, and calculation-heavy retrieval.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: dlcRgba('ptcb', 0.12) }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DLC_COLORS.ptcb.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </div>
              <h3>Workflow Synthesis</h3>
              <p>
                Cross-step nodes for input, fill, verify, and counsel sequences.
                Learn the process, not just isolated facts.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: dlcRgba('ptcb', 0.12) }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DLC_COLORS.ptcb.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h3>Safety & QA Checks</h3>
              <p>
                Force the technician mindset: catch errors, identify unsafe fills,
                manage recalls, and keep the workflow compliant under pressure.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: dlcRgba('ptcb', 0.12) }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DLC_COLORS.ptcb.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
              <h3>Scenario Rounds</h3>
              <p>
                Applied patient and operations cases: refill too soon, inventory
                conflict, insurance rejection, or compounding workflow breakdowns.
              </p>
            </div>

            <div className="naplex-prep-node-card naplex-animate">
              <div className="naplex-prep-node-icon" style={{ background: dlcRgba('ptcb', 0.14) }}>
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DLC_COLORS.ptcb.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <h3>Boss Fights</h3>
              <p>
                Domain bosses, synthesis bosses, and final workflow clashes that turn
                the exam blueprint into a repeatable combat loop instead of a dead bank.
              </p>
            </div>
          </div>
        </section>

        <section className="naplex-prep-section">
          <div className="naplex-prep-section-header naplex-animate">
            <h2>Not Another Static PTCB Bank</h2>
            <p>
              Most technician prep tools become memory drills once you finish the bank.
              This stays procedural: new prompts, new angles, same blueprint.
            </p>
          </div>

          <div className="naplex-prep-compare">
            <div className="naplex-prep-compare-card naplex-prep-compare-card--them naplex-animate">
              <div className="naplex-prep-compare-label">Traditional Banks</div>
              <ul className="naplex-prep-compare-list">
                <li><XIcon /> Fixed question pool that gets stale fast</li>
                <li><XIcon /> Weak workflow simulation and almost no pressure feedback</li>
                <li><XIcon /> Generic review that treats every miss the same</li>
                <li><XIcon /> Little connection between medications, law, and QA</li>
                <li><XIcon /> Monthly renewals just to keep drilling</li>
              </ul>
            </div>
            <div className="naplex-prep-compare-card naplex-prep-compare-card--us naplex-animate">
              <div className="naplex-prep-compare-label">Debono PTCB DLC</div>
              <ul className="naplex-prep-compare-list">
                <li><CheckIcon /> AI generates fresh questions from seeded domain content</li>
                <li><CheckIcon /> Boss fights and maps adapt to your actual weak spots</li>
                <li><CheckIcon /> Workflow logic, law, and meds stay connected</li>
                <li><CheckIcon /> One free domain to test the loop before you unlock all four</li>
                <li><CheckIcon /> $35 once for permanent access</li>
              </ul>
            </div>
          </div>
        </section>

        <MobileDesktopBanner
          page="ptcb-prep"
          headline="Debono's PTCB prep works best on desktop."
          subtext="Drop your email and we'll send you the link. Takes 2 seconds."
        />

        <section className="naplex-prep-bottom-cta naplex-animate">
          <h2>One price. Four PTCB domains. Unlimited replays.</h2>
          <p>
            Permanent access to medications, law, safety, and order-entry training without a recurring bill.
          </p>
          <div className="naplex-prep-price-display">
            <span className="naplex-prep-price-amount">$35</span>
            <span className="naplex-prep-price-period">one-time</span>
          </div>
          <p className="naplex-prep-price-note">
            Includes all four PTCB domains. Start with one free domain, then unlock the full board.
          </p>
          <div className="naplex-prep-hero-cta">
            <button className="naplex-prep-btn-primary" onClick={handleCta}>
              {user?.ptcb_dlc ? 'Already Unlocked' : (isAuthenticated ? 'Get PTCB DLC' : 'Get Started')}
            </button>
            <Link to="/pricing" className="naplex-prep-btn-ghost">
              Compare Plans
            </Link>
          </div>
        </section>

        <footer className="naplex-prep-footer">
          <div className="naplex-prep-footer-links">
            <Link to="/">Home</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
